import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { mediaPortalApi, exercisesApi } from '../../lib/api';
import { postsApi as mpPostsApi, platformsApi as mpPlatformsApi } from '../../lib/mpApi';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import SocialPostTemplate, { NewsTemplate } from '../mp/SocialPostTemplates';
import NewsArticleDetailDialog from '../mp/NewsArticleDetailDialog';
import {
  Radio, Link2, Unlink, Globe, Send, Clock, Eye,
  Settings, Newspaper, RefreshCw, ExternalLink, BarChart3,
  TrendingUp, MessageSquare, Share2, Heart, Play, Zap,
  CheckCircle2, Circle, Plus, CalendarClock, ChevronDown, ChevronUp,
  ArrowUpRight, Monitor,
} from 'lucide-react';

/* ============================================================
   EMBEDDED MEDIA PORTAL — Full MP Dashboard inside EMS Exercise
   ============================================================ */

const AnalyticsMini = ({ analytics }) => {
  if (!analytics) return null;
  const cards = [
    { label: 'Total Posts', value: analytics.total_posts, icon: Newspaper, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Published', value: analytics.published, icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Scheduled', value: analytics.scheduled, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Drafts', value: analytics.draft, icon: Circle, color: 'text-muted-foreground', bg: 'bg-slate-400/10' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mp-embed-analytics">
      {cards.map(c => (
        <div key={c.label} className="p-3 rounded-xl border bg-card/50">
          <div className="flex items-center gap-2 mb-1">
            <div className={`p-1 rounded-lg ${c.bg}`}><c.icon className={`w-3.5 h-3.5 ${c.color}`} /></div>
          </div>
          <p className="text-xl font-bold tracking-tight">{c.value ?? 0}</p>
          <p className="text-[11px] text-muted-foreground">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

const EngagementMini = ({ engagement }) => {
  if (!engagement) return null;
  const metrics = [
    { label: 'Views', value: engagement.views ?? 0, icon: Eye, color: 'text-blue-400' },
    { label: 'Likes', value: engagement.likes ?? 0, icon: Heart, color: 'text-rose-400' },
    { label: 'Comments', value: engagement.comments ?? 0, icon: MessageSquare, color: 'text-purple-400' },
    { label: 'Shares', value: engagement.shares ?? 0, icon: Share2, color: 'text-teal-400' },
  ];
  return (
    <div className="grid grid-cols-4 gap-3" data-testid="mp-embed-engagement">
      {metrics.map(m => (
        <div key={m.label} className="text-center p-2 rounded-lg border bg-card/30">
          <m.icon className={`w-4 h-4 mx-auto mb-0.5 ${m.color}`} />
          <p className="text-sm font-bold">{(m.value ?? 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">{m.label}</p>
        </div>
      ))}
    </div>
  );
};

const PlatformBreakdownMini = ({ byPlatform, platforms }) => {
  if (!byPlatform || Object.keys(byPlatform).length === 0) return null;
  const total = Object.values(byPlatform).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-1.5" data-testid="mp-embed-platforms">
      {Object.entries(byPlatform).sort((a, b) => b[1] - a[1]).map(([plat, count]) => {
        const p = platforms.find(x => x.name === plat);
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={plat} className="flex items-center gap-2 text-sm">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color || '#666' }} />
            <span className="flex-1 truncate text-xs">{p?.display_name || plat}</span>
            <span className="text-xs font-medium tabular-nums">{count}</span>
            <div className="w-14 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: p?.color || '#666' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PostQueueMini = ({ posts, platforms, onPublish, publishingId }) => {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const filtered = posts.filter(p => filter === 'all' || p.status === filter);

  const statusConfig = {
    published: { label: 'Published', badge: 'default' },
    scheduled: { label: 'Scheduled', badge: 'outline' },
    draft: { label: 'Draft', badge: 'secondary' },
  };

  return (
    <div data-testid="mp-embed-posts">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium flex items-center gap-1.5">
          <Newspaper className="w-3.5 h-3.5" /> Posts ({posts.length})
        </h4>
        <div className="flex gap-1">
          {['all', 'published', 'scheduled', 'draft'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >{f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
      </div>

      <ScrollArea className="max-h-[400px]">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            <Send className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
            <p className="text-xs">No posts {filter !== 'all' ? `with status "${filter}"` : 'yet'}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {filtered.slice(0, 20).map(post => {
              const plat = platforms.find(p => p.name === post.platform);
              const sc = statusConfig[post.status] || statusConfig.draft;
              const isExpanded = expanded === post.post_id;
              return (
                <div key={post.post_id} className="rounded-lg border bg-card hover:border-primary/20 transition-colors overflow-hidden">
                  <div className="p-2.5 flex items-center gap-2.5 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : post.post_id)}>
                    <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: (plat?.color || '#666') + '18' }}>
                      <Globe className="w-3.5 h-3.5" style={{ color: plat?.color || '#666' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{post.title || post.content?.slice(0, 60) || 'Untitled'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {plat?.display_name || post.platform}
                        {post.schedule?.publish_datetime && <> &middot; <Clock className="w-2.5 h-2.5 inline" /> {new Date(post.schedule.publish_datetime).toLocaleString()}</>}
                      </p>
                    </div>
                    <Badge variant={sc.badge} className="text-[10px] px-1.5">{sc.label}</Badge>
                    {post.status !== 'published' && onPublish && (
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-emerald-600"
                        onClick={e => { e.stopPropagation(); onPublish(post.post_id); }}
                        disabled={publishingId === post.post_id}>
                        {publishingId === post.post_id
                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                          : <Play className="w-3 h-3" />}
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                  </div>
                  {isExpanded && (
                    <div className="px-2.5 pb-2.5 border-t pt-2 text-xs text-muted-foreground space-y-1">
                      <p className="whitespace-pre-wrap">{post.content}</p>
                      <div className="flex items-center gap-3">
                        <span>By: {post.author?.name || 'Unknown'}</span>
                        <span>Created: {new Date(post.created_at).toLocaleString()}</span>
                        {post.ems_inject_id && <span className="flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> From inject</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

const CreatePostMini = ({ exerciseId, platforms, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', content: '', platform: '', scheduled: false, publish_datetime: '',
  });

  const handleCreate = async () => {
    if (!form.content.trim()) { toast.error('Post content is required'); return; }
    setCreating(true);
    try {
      await mediaPortalApi.createPost(exerciseId, form);
      toast.success(form.scheduled ? 'Post scheduled!' : 'Post published!');
      setForm({ title: '', content: '', platform: '', scheduled: false, publish_datetime: '' });
      setOpen(false);
      onCreated();
    } catch (err) { toast.error(err.message || 'Failed to create post'); }
    finally { setCreating(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="mp-embed-create-post-btn">
        <Plus className="w-3.5 h-3.5 mr-1.5" /> New Post
      </Button>
    );
  }

  return (
    <div className="rounded-xl border border-primary/20 p-4 space-y-3 bg-card" data-testid="mp-embed-create-post-form">
      <h4 className="text-sm font-medium flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Create Media Post</h4>
      <Input placeholder="Post title (optional)" value={form.title}
        onChange={e => setForm({ ...form, title: e.target.value })} className="h-8 text-sm" />
      <Textarea placeholder="Post content..." rows={3} value={form.content}
        onChange={e => setForm({ ...form, content: e.target.value })} className="text-sm" />
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[150px]">
          <Label className="text-xs">Platform</Label>
          <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Default" /></SelectTrigger>
            <SelectContent>
              {platforms.map(p => <SelectItem key={p.platform_id || p.name} value={p.name}>{p.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.scheduled} onCheckedChange={v => setForm({ ...form, scheduled: v })} />
          <Label className="text-xs flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Schedule</Label>
        </div>
        {form.scheduled && (
          <div className="min-w-[180px]">
            <Label className="text-xs">Publish Date/Time</Label>
            <Input type="datetime-local" value={form.publish_datetime} className="h-8 text-xs"
              onChange={e => setForm({ ...form, publish_datetime: e.target.value })} />
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 text-xs">Cancel</Button>
        <Button size="sm" onClick={handleCreate} disabled={creating} className="h-7 text-xs"
          data-testid="mp-embed-post-submit">
          {creating ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
          {form.scheduled ? 'Schedule' : 'Publish Now'}
        </Button>
      </div>
    </div>
  );
};

const SettingsPanel = ({ settings, setSettings, platforms, onSave, guestToken }) => (
  <div className="rounded-xl border border-primary/20 p-4 space-y-3 bg-card" data-testid="mp-embed-settings">
    <h4 className="text-sm font-medium flex items-center gap-1.5"><Settings className="w-3.5 h-3.5" /> Integration Settings</h4>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <Label className="text-xs">Default Platform</Label>
        <Select value={settings.default_platform || ''} onValueChange={v => setSettings({ ...settings, default_platform: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>{platforms.map(p => <SelectItem key={p.platform_id || p.name} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 pt-4">
        <Switch checked={settings.auto_publish || false} onCheckedChange={v => setSettings({ ...settings, auto_publish: v })} />
        <div><Label className="text-xs">Auto-publish on inject</Label><p className="text-[10px] text-muted-foreground">Injects auto-post to MP</p></div>
      </div>
      <div>
        <Label className="text-xs">Default Author</Label>
        <Input className="h-8 text-xs" value={settings.default_author?.name || ''}
          onChange={e => setSettings({ ...settings, default_author: { ...settings.default_author, name: e.target.value } })} />
      </div>
    </div>
    <div className="flex items-center justify-between">
      {guestToken && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ExternalLink className="w-3 h-3" /> Guest token: <code className="bg-muted px-1.5 py-0.5 rounded text-[10px]">{guestToken}</code>
        </div>
      )}
      <Button size="sm" onClick={onSave} className="h-7 text-xs">Save Settings</Button>
    </div>
  </div>
);

/* ============================================================ */

const NEWS_PLATFORM_COLORS = {
  cnn: '#CC0000', bbc: '#BB1919', bbc_news: '#BB1919', reuters: '#FF8000',
  al_jazeera: '#D2691E', fox_news: '#003366', sky_news: '#9B0000',
  the_guardian: '#005689', nyt: '#1A1A1A', washington_post: '#333',
};

const isNewsPlatform = (name) => {
  const n = (name || '').toLowerCase();
  return ['cnn', 'bbc', 'bbc_news', 'reuters', 'al_jazeera', 'fox_news', 'sky_news', 'the_guardian', 'nyt', 'washington_post', 'news'].some(k => n.includes(k));
};

const LivePreviewFeed = ({ posts, platforms }) => {
  const [previewOpen, setPreviewOpen] = useState(true);
  const [detailPost, setDetailPost] = useState(null);
  const published = posts.filter(p => p.status === 'published');

  // Group posts by platform
  const platformGroups = {};
  published.forEach(p => {
    const key = p.platform || 'unknown';
    if (!platformGroups[key]) platformGroups[key] = [];
    platformGroups[key].push(p);
  });

  const platformNames = Object.keys(platformGroups);
  if (platformNames.length === 0 && published.length === 0) return null;

  return (
    <Card data-testid="mp-embed-live-preview">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Monitor className="w-3.5 h-3.5 text-red-500" />
            Live Preview
            <Badge variant="secondary" className="text-[10px] ml-1">{published.length} posts</Badge>
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
            onClick={() => setPreviewOpen(v => !v)}>
            {previewOpen ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            {previewOpen ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </CardHeader>
      {previewOpen && (
        <CardContent className="px-4 pb-4 pt-0">
          {published.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Radio className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
              <p className="text-xs">No published posts to preview yet</p>
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2" style={{ minHeight: '200px' }}>
              {platformNames.map(platName => {
                const platPosts = platformGroups[platName];
                const plat = platforms.find(p => p.name === platName);
                // V14.13.11.3 — category-aware news detection.
                const platIsNews = plat?.category === 'news' || isNewsPlatform(platName);
                const color = plat?.color || NEWS_PLATFORM_COLORS[platName.toLowerCase()] || '#666';
                return (
                  <div key={platName} className="flex-shrink-0 border-r last:border-r-0 border-border pr-3"
                    style={{ width: platformNames.length === 1 ? '100%' : `${Math.max(280, 100 / platformNames.length)}%`, minWidth: '280px', maxWidth: '400px' }}>
                    {/* Column header */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                      <div className="w-5 h-5 rounded flex items-center justify-center text-white text-[9px] font-bold"
                        style={{ backgroundColor: color }}>
                        {(plat?.display_name || platName).charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wide">{plat?.display_name || platName}</span>
                      <Badge variant="secondary" className="text-[10px] ml-auto">{platPosts.length}</Badge>
                    </div>
                    {/* Posts */}
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3 pr-2">
                        {platPosts.slice(0, 10).map(post => {
                          const isNews = platIsNews || post.post_type === 'news';
                          return (
                            <div key={post.post_id} className="transition-all">
                              {isNews
                                ? <NewsTemplate post={post} platformColor={color} onOpen={() => setDetailPost(post)} />
                                : <SocialPostTemplate post={post} platform={post.platform} onOpen={() => setDetailPost(post)} />}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
      <NewsArticleDetailDialog open={!!detailPost} onOpenChange={(o) => !o && setDetailPost(null)} post={detailPost} />
    </Card>
  );
};

export default function EmbeddedMediaPortal({ exerciseId, exercise }) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('super_admin');
  const isObserver = user?.roles?.includes('observer') && !isAdmin && !user?.roles?.includes('simcel');
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [postQueue, setPostQueue] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const refreshRef = useRef(null);

  const loadDetails = useCallback(async () => {
    try {
      const [status, sett, analyticsData, queue, plats] = await Promise.all([
        mediaPortalApi.getBridgeStatus(exerciseId),
        mediaPortalApi.getSettings(exerciseId).catch(() => null),
        mediaPortalApi.getAnalytics(exerciseId).catch(() => null),
        mediaPortalApi.getPostQueue(exerciseId).catch(() => ({ posts: [] })),
        mediaPortalApi.listPlatforms().catch(() => []),
      ]);
      setBridgeStatus(status);
      setSettings(sett);
      setAnalytics(analyticsData);
      setPostQueue(queue.posts || []);
      // V14.11.1 — Filter platforms to only those selected for the linked MP
      // exercise. `selected_platforms` may store EITHER platform IDs OR names
      // (legacy / mixed data), so we match against both fields. If no platform
      // has been selected yet OR the filter would yield zero matches, fall
      // back to ALL platforms so the dropdown is never empty.
      const selectedIds = status?.selected_platforms || [];
      const filtered = selectedIds.length > 0
        ? plats.filter(p => selectedIds.includes(p.platform_id) || selectedIds.includes(p.name))
        : plats;
      setPlatforms(filtered.length > 0 ? filtered : plats);
    } catch {
      setBridgeStatus(null);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => {
    loadDetails();
    refreshRef.current = setInterval(loadDetails, 10000);
    return () => clearInterval(refreshRef.current);
  }, [loadDetails]);

  const [mpExercises, setMpExercises] = useState([]);
  const [selectedMpEx, setSelectedMpEx] = useState('');
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  const handleLink = async (mpExId) => {
    setLinking(true);
    try {
      const url = mpExId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/media-portal/link-exercise/${exerciseId}?mp_exercise_id=${encodeURIComponent(mpExId)}`
        : undefined;
      if (url) {
        const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` } });
        if (!res.ok) throw new Error('Failed to link');
      } else {
        await mediaPortalApi.linkExercise(exerciseId);
      }
      toast.success('Media Portal exercise linked!');
      setShowLinkPicker(false);
      await loadDetails();
    } catch (err) { toast.error(err.message || 'Failed to link'); }
    finally { setLinking(false); }
  };

  const openLinkPicker = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mp/exercises`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` }
      });
      const data = await res.json();
      setMpExercises(Array.isArray(data) ? data : data.exercises || []);
    } catch { setMpExercises([]); }
    setShowLinkPicker(true);
  };

  const handleSaveSettings = async () => {
    try {
      await mediaPortalApi.updateSettings(exerciseId, settings);
      toast.success('Settings saved');
      setSettingsOpen(false);
      await loadDetails();
    } catch (err) { toast.error(err.message || 'Failed to save'); }
  };

  const handlePublish = async (postId) => {
    setPublishingId(postId);
    try {
      await mediaPortalApi.publishPost(postId);
      toast.success('Post published!');
      await loadDetails();
    } catch (err) { toast.error(err.message || 'Failed to publish'); }
    finally { setPublishingId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  const handleUnlink = async () => {
    setLinking(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-portal/unlink-exercise/${exerciseId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` },
      });
      if (!res.ok) throw new Error('Failed to unlink');
      toast.success('Media Portal unlinked. You can now link a new exercise.');
      await loadDetails();
    } catch (err) { toast.error(err.message || 'Failed to unlink'); }
    finally { setLinking(false); }
  };

  /* Orphaned link — MP exercise was deleted */
  if (bridgeStatus?.linked && bridgeStatus?.orphaned) {
    return (
      <div className="text-center py-14 border rounded-xl bg-destructive/5 border-destructive/20" data-testid="mp-embed-orphaned">
        <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <Unlink className="w-7 h-7 text-destructive/70" />
        </div>
        <h3 className="text-base font-semibold mb-1 text-destructive">Media Portal Link Broken</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
          The linked Media Portal exercise has been deleted. Unlink to reconnect to a new or existing exercise.
        </p>
        {isAdmin && (
          <div className="flex gap-2 justify-center">
            <Button variant="destructive" onClick={handleUnlink} disabled={linking} data-testid="mp-embed-unlink-btn">
              <Unlink className="w-4 h-4 mr-2" />
              {linking ? 'Unlinking...' : 'Unlink & Reset'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  /* Not linked yet */
  if (!bridgeStatus?.linked) {
    return (
      <div className="text-center py-14 border rounded-xl bg-muted/5" data-testid="mp-embed-not-linked">
        <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
          <Unlink className="w-7 h-7 text-muted-foreground/50" />
        </div>
        <h3 className="text-base font-semibold mb-1">Media Portal Not Linked</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
          Connect this exercise to an existing Media Portal exercise or create a new one.
        </p>
        {isAdmin && !showLinkPicker && (
          <Button onClick={openLinkPicker} disabled={linking} data-testid="mp-embed-link-btn">
            <Link2 className="w-4 h-4 mr-2" />
            {linking ? 'Linking...' : 'Link to Media Portal'}
          </Button>
        )}
        {showLinkPicker && (
          <div className="max-w-sm mx-auto text-left space-y-3 mt-2">
            {mpExercises.length > 0 && (
              <>
                <Label className="text-xs">Select existing MP exercise:</Label>
                <Select value={selectedMpEx} onValueChange={setSelectedMpEx}>
                  <SelectTrigger className="h-9 text-sm" data-testid="mp-exercise-picker">
                    <SelectValue placeholder="Choose an exercise..." />
                  </SelectTrigger>
                  <SelectContent>
                    {mpExercises.map(ex => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name} ({ex.status || 'draft'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full" disabled={!selectedMpEx || linking} onClick={() => handleLink(selectedMpEx)} data-testid="mp-link-existing-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Link Selected Exercise
                </Button>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><div className="flex-1 border-t" /><span>or</span><div className="flex-1 border-t" /></div>
              </>
            )}
            <Button variant="outline" className="w-full" disabled={linking} onClick={() => handleLink(null)} data-testid="mp-create-new-btn">
              Create New MP Exercise
            </Button>
          </div>
        )}
      </div>
    );
  }

  /* Linked — full embedded dashboard */
  return (
    <div className="space-y-4" data-testid="mp-embed-dashboard">
      {/* Status bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl border bg-card/50" data-testid="mp-embed-status">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {bridgeStatus.mp_exercise_status === 'enabled' && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
              bridgeStatus.mp_exercise_status === 'enabled' ? 'bg-emerald-500' : 'bg-slate-400'
            }`} />
          </span>
          <span className="text-xs font-medium">{bridgeStatus.mp_exercise_status === 'enabled' ? 'LIVE' : (bridgeStatus.mp_exercise_status || 'OFFLINE').toUpperCase()}</span>
        </div>
        <div className="h-3.5 w-px bg-border" />
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Radio className="w-3 h-3" />{bridgeStatus.mp_exercise_name}</span>
        <div className="h-3.5 w-px bg-border" />
        <span className="text-xs text-muted-foreground"><Newspaper className="w-3 h-3 inline mr-0.5" />{bridgeStatus.post_count ?? 0} posts</span>
        {bridgeStatus.auto_publish && (
          <>
            <div className="h-3.5 w-px bg-border" />
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-emerald-600 border-emerald-300"><Zap className="w-2.5 h-2.5 mr-0.5" /> Auto</Badge>
          </>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSettingsOpen(v => !v)}>
              <Settings className="w-3 h-3 mr-1" /> Settings
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={loadDetails}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Link to="/media-portal">
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
              <ArrowUpRight className="w-3 h-3 mr-1" /> Full View
            </Button>
          </Link>
        </div>
      </div>

      {/* Settings panel */}
      {settingsOpen && settings && (
        <SettingsPanel settings={settings} setSettings={setSettings} platforms={platforms}
          onSave={handleSaveSettings} guestToken={bridgeStatus.guest_token} />
      )}

      {/* Analytics */}
      <AnalyticsMini analytics={analytics} />

      {/* Engagement + Platforms */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Engagement</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <EngagementMini engagement={analytics?.engagement} />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-blue-500" /> Platforms</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <PlatformBreakdownMini byPlatform={analytics?.by_platform} platforms={platforms} />
          </CardContent>
        </Card>
      </div>

      {/* Create post */}
      {!isObserver && <CreatePostMini exerciseId={exerciseId} platforms={platforms} onCreated={loadDetails} />}

      {/* Post queue */}
      <PostQueueMini posts={postQueue} platforms={platforms} onPublish={isObserver ? null : handlePublish} publishingId={publishingId} />

      {/* Live Preview Feed */}
      <LivePreviewFeed posts={postQueue} platforms={platforms} />
    </div>
  );
}
